import { getAnnotationColor } from "@shared/features/trades/common/utils";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import React, { ReactNode } from "react";
import styled, { css, useTheme } from "styled-components";
import { Flame } from "lucide-react";
import {
  ActiveTradeNextOrder,
  Direction,
  LanguageNameSpaces,
  TradeData,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useNextOrder from "@shared/common/LiveComponents/useNextOrder";
import { OrderTypes } from "@shared/features/trades/common/types";
import { DOLLAR_COINS } from "@shared/common/constants";
import { AccountObjectType } from "@shared/features/settings/types";
import { TradeIdsToPnlMapping } from "@shared/services/socket/types";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import useValueFlash from "common/NewLook/useValueFlash.ts";
import Skeleton from "common/NewLook/Skeleton.tsx";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import { NEW_LOOK_COMPACT_TABLE_CLASS } from "features/trades/common/constants";
import useMoneyFormat, {
  formatSignedPercent,
  MINUS,
  shownPercent,
} from "features/trades/NewLook/useMoneyFormat.ts";
import { translateOptionName } from "@shared/common/i18n/optionNames";

/**
 * Cells of the new-look trades table, in the dashboard's roles: the symbol
 * over a "Long 10x · account" line, figures (15/500, tabular) over a quiet
 * meta line, and colour only where it means something.
 */

interface SymbolCellProps {
  trade: TradeData;
  isHot?: boolean;
}

export const SymbolCell = ({ trade, isHot }: SymbolCellProps) => {
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const side = trade["Long/Short"];
  const isShort = side === Direction.SHORT;
  const leverage = trade.leverage
    ? [`${trade.leverage}x`, translateOptionName(trade.margin_type)]
        .filter(Boolean)
        .join(" ")
    : undefined;
  return (
    <Pair>
      <IconWithSource
        symbol={trade.symbol}
        sourceType={trade.source_type}
        source={trade.group}
        mainIconSize={28}
        style={ICON_STYLE}
      />
      <Names>
        <SymbolLine>
          <InactiveAccountIcon
            accountId={trade.client_id}
            objectType={AccountObjectType.TRADE}
          >
            <SymbolName>{trade.symbol}</SymbolName>
          </InactiveAccountIcon>
          {isHot && (
            <Flame
              size={12}
              strokeWidth={2.25}
              color={tokens.stop}
              aria-label={tCommon("labels.hot_trade")}
            />
          )}
        </SymbolLine>
        <SubLine>
          {side && (
            <Side style={{ color: isShort ? tokens.down : tokens.up }}>
              {translateOptionName(side)}
            </Side>
          )}
          {leverage && <Leverage>{leverage}</Leverage>}
          <InactiveAccountIcon
            accountId={trade.client_id}
            objectType={AccountObjectType.ACCOUNT}
            hideIcon={true}
          >
            <AccountName title={trade.account} $dot={!!side || !!leverage}>
              {trade.account}
            </AccountName>
          </InactiveAccountIcon>
        </SubLine>
      </Names>
    </Pair>
  );
};

interface FigureCellProps {
  value: ReactNode;
  meta?: ReactNode;
  color?: string;
  metaColor?: string;
  className?: string;
  metaTestId?: string;
}

/** A figure over a meta line, right-aligned for comparison down the column. */
export const FigureCell = ({
  value,
  meta,
  color,
  metaColor,
  className,
  metaTestId,
}: FigureCellProps) => (
  <End>
    <Figure className={className} style={color ? { color } : undefined}>
      {value}
    </Figure>
    {meta !== undefined && meta !== null && (
      <Meta
        style={metaColor ? { color: metaColor } : undefined}
        data-testid={metaTestId}
      >
        {meta}
      </Meta>
    )}
  </End>
);

interface NextOrderCellProps {
  trade: TradeData;
  nextOrder: ActiveTradeNextOrder | undefined;
  orderType: OrderTypes;
}

/**
 * The distance to the next order as the figure, its price below. The order
 * type shows as a small dot in its ladder colour (entry, take-profit, stop);
 * the figure stays ink, so red and green keep meaning loss and profit.
 */
export const NextOrderCell = ({
  trade,
  nextOrder,
  orderType,
}: NextOrderCellProps) => {
  const theme = useTheme();
  const { pair, priceText, pricePrecision, distanceText } = useNextOrder({
    symbol: trade.symbol,
    exchange: trade.exchange,
    nextOrder,
    orderType,
  });
  if (!nextOrder)
    return (
      <End>
        <Meta>—</Meta>
      </End>
    );
  const distanceValue = parseFloat(distanceText);
  const distance = distanceValue
    ? `${distanceValue > 0 ? "+" : MINUS}${distanceText.replace(/^-/, "")}`
    : distanceText;
  const price = getLocaleStringWithPrecision(Number(priceText), pricePrecision);
  const isDollar = !!pair && DOLLAR_COINS.includes(pair);
  return (
    <FigureCell
      value={distance}
      // The distance takes its order type's colour (entry, take-profit,
      // stop), so the column scans by meaning; the price stays muted.
      color={getAnnotationColor(orderType, theme)}
      meta={isDollar ? `$${price}` : `${price} ${pair || ""}`.trim()}
    />
  );
};

interface PnlCellProps {
  trade: TradeData;
  socketPnlData?: TradeIdsToPnlMapping;
  amountChangeHeader: "btc_amount_change" | "usd_amount_change";
}

/** Live PnL in the display currency, its % below, both by direction. */
export const PnlCell = ({
  trade,
  socketPnlData,
  amountChangeHeader,
}: PnlCellProps) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { signedMoney, shown } = useMoneyFormat();
  const live = socketPnlData?.[trade.id];
  const pnl = Number((live || trade)?.[amountChangeHeader] ?? 0) || 0;
  const graphData = (live || trade)?.graph_data;
  const percent =
    graphData && "profit" in graphData && graphData.profit !== undefined
      ? Number(graphData.profit)
      : undefined;
  const flash = useValueFlash(pnl);
  return (
    <FigureCell
      value={signedMoney(pnl)}
      className={flash}
      color={directionColor(tokens, shown(pnl))}
      meta={percent !== undefined ? formatSignedPercent(percent) : undefined}
      metaColor={
        percent !== undefined
          ? directionColor(tokens, shownPercent(percent))
          : undefined
      }
    />
  );
};

/** A date in the body role; for open trades the full date on hover. */
export const TimeCell = ({ children }: { children: ReactNode }) => (
  <Time>{children}</Time>
);

/** Loading rows shaped like the table's rows. */
export const TradeRowsSkeleton = ({ rows = 6 }: { rows?: number }) => (
  <SkeletonRows aria-busy={true}>
    {Array.from({ length: rows }, (_, index) => (
      <SkeletonRow key={index}>
        <Pair>
          <Skeleton width={28} height={28} radius={14} />
          <Names>
            <Skeleton width={84} height={12} />
            <Skeleton width={120} height={10} />
          </Names>
        </Pair>
        <Skeleton width={72} height={12} />
        <Skeleton width={72} height={12} />
        <Skeleton width={64} height={12} />
        <Skeleton width={164} height={8} radius={4} />
      </SkeletonRow>
    ))}
  </SkeletonRows>
);

// The source badge overhangs the coin by a few pixels; the gap does the rest.
const ICON_STYLE = { flex: "none", marginRight: 4 };

const Pair = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const SymbolLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;
const SymbolName = styled.span`
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const SubLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 200px;
  @container nl-trades (min-width: 1300px) {
    max-width: 280px;
  }
  .${NEW_LOOK_COMPACT_TABLE_CLASS} & {
    max-width: 150px;
  }
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  /* The inactive-account wrapper can shrink with the account name. */
  > * {
    min-width: 0;
  }
`;
const Side = styled.span`
  flex: none;
  font-weight: 500;
`;
const Leverage = styled.span`
  flex: none;
`;
const AccountName = styled.span<{ $dot?: boolean }>`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  ${(p) =>
    p.$dot &&
    css`
      &::before {
        content: "·";
        margin-right: 6px;
      }
    `}
`;
const End = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 0;
  text-align: right;
`;
const Figure = styled.span`
  ${newLookType.figure};
  /* A touch lighter than the figure role: a whole column of figures reads
     heavy at 500 (Inter is variable, so 450 renders). */
  font-weight: 450;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  padding: 0 3px;
  margin: 0 -3px;
  border-radius: 4px;
  white-space: nowrap;
  /* Shared value components (amount with precision) take the role. */
  * {
    font-size: inherit !important;
    line-height: inherit !important;
    font-weight: inherit !important;
    color: inherit !important;
  }
`;
const Meta = styled.span`
  ${newLookType.meta};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  * {
    font-size: inherit !important;
    line-height: inherit !important;
    color: inherit !important;
  }
`;
const Time = styled.span`
  ${newLookType.body};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
`;
const SkeletonRows = styled.div`
  display: flex;
  flex-direction: column;
`;
const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) repeat(3, minmax(72px, 0.7fr)) 180px;
  align-items: center;
  column-gap: 24px;
  min-height: 56px;
  padding: 10px 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  > :not(:first-child) {
    justify-self: end;
  }
  > :last-child {
    justify-self: start;
  }
`;
